/**
 * Training Mode state + hooks.
 *
 * Single source of truth for Training Mode runtime state (enabled/disabled,
 * active topic, picker open/closed, floating panel minimized, overlay stack
 * for auto-minimize, and chat session id).
 *
 * Session-scoped: persists for the current tab session via `sessionStorage`
 * (so a full-page reload inside a session keeps training on). Resets on
 * logout (AuthContext clears localStorage → banner hides as a side effect).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { resolveTopicForPath } from "@/lib/trainingRouteMap";

const TrainingContext = createContext(null);

const SS_KEY_ENABLED = "training.mode.enabled";
const SS_KEY_TOPIC_SLUG = "training.mode.activeTopicSlug";

export function TrainingProvider({ children }) {
  const [enabled, setEnabledState] = useState(() => {
    try { return sessionStorage.getItem(SS_KEY_ENABLED) === "1"; } catch { return false; }
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeTopic, setActiveTopicState] = useState(null); // topic object
  const [activeTopicSlug, setActiveTopicSlugState] = useState(() => {
    try { return sessionStorage.getItem(SS_KEY_TOPIC_SLUG) || null; } catch { return null; }
  });
  const [panelMinimized, setPanelMinimized] = useState(false);
  const [overlayCount, setOverlayCount] = useState(0);
  const [sessionId] = useState(() => `training-${Math.random().toString(36).slice(2, 10)}`);

  // Keep sessionStorage in sync with enabled/activeTopicSlug
  useEffect(() => {
    try {
      if (enabled) sessionStorage.setItem(SS_KEY_ENABLED, "1");
      else sessionStorage.removeItem(SS_KEY_ENABLED);
    } catch {
      /* noop */
    }
  }, [enabled]);

  useEffect(() => {
    try {
      if (activeTopicSlug) sessionStorage.setItem(SS_KEY_TOPIC_SLUG, activeTopicSlug);
      else sessionStorage.removeItem(SS_KEY_TOPIC_SLUG);
    } catch {
      /* noop */
    }
  }, [activeTopicSlug]);

  const setEnabled = useCallback((val) => {
    setEnabledState((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (!next) {
        // Turning OFF — clear all training UI state
        setPickerOpen(false);
        setActiveTopicState(null);
        setActiveTopicSlugState(null);
        setPanelMinimized(false);
        setOverlayCount(0);
      } else if (next && !prev) {
        // Turning ON — open picker immediately (spec §1B step 2)
        setPickerOpen(true);
      }
      return next;
    });
  }, []);

  const setActiveTopic = useCallback((topic) => {
    setActiveTopicState(topic);
    setActiveTopicSlugState(topic?.slug || null);
  }, []);

  const pushOverlay = useCallback(() => setOverlayCount((c) => c + 1), []);
  const popOverlay = useCallback(() => setOverlayCount((c) => Math.max(0, c - 1)), []);

  // Auto-minimize floating panel when any overlay is open (spec §2.auto-minimize)
  const effectiveMinimized = panelMinimized || overlayCount > 0;

  const value = useMemo(() => ({
    enabled, setEnabled,
    pickerOpen, setPickerOpen,
    activeTopic, setActiveTopic,
    activeTopicSlug,
    panelMinimized, setPanelMinimized, effectiveMinimized,
    overlayCount, pushOverlay, popOverlay,
    sessionId,
  }), [
    enabled, setEnabled,
    pickerOpen, activeTopic, activeTopicSlug,
    panelMinimized, effectiveMinimized, overlayCount,
    pushOverlay, popOverlay, sessionId, setActiveTopic,
  ]);

  return (
    <TrainingContext.Provider value={value}>{children}</TrainingContext.Provider>
  );
}

export function useTraining() {
  const ctx = useContext(TrainingContext);
  if (!ctx) {
    // Fail-closed: when provider isn't mounted (e.g. /login), return a
    // no-op stub so components can still render without throwing.
    return {
      enabled: false, setEnabled: () => {},
      pickerOpen: false, setPickerOpen: () => {},
      activeTopic: null, setActiveTopic: () => {},
      activeTopicSlug: null,
      panelMinimized: false, setPanelMinimized: () => {},
      effectiveMinimized: false,
      overlayCount: 0, pushOverlay: () => {}, popOverlay: () => {},
      sessionId: "training-noop",
    };
  }
  return ctx;
}

/**
 * useOverlayAutoMinimize — call this inside any modal/lightbox/drawer
 * component to auto-minimize the training floating panel while it is
 * mounted. Opt-in, non-invasive.
 */
export function useOverlayAutoMinimize(open) {
  const { pushOverlay, popOverlay } = useTraining();
  const activeRef = useRef(false);
  useEffect(() => {
    if (open && !activeRef.current) {
      activeRef.current = true;
      pushOverlay();
    } else if (!open && activeRef.current) {
      activeRef.current = false;
      popOverlay();
    }
    return () => {
      if (activeRef.current) {
        activeRef.current = false;
        popOverlay();
      }
    };
  }, [open, pushOverlay, popOverlay]);
}

export { resolveTopicForPath };
